import React, { useState, useEffect, useCallback } from "react";
import {
  FiUsers,
  FiPlus,
  FiDownload,
  FiTrash2,
  FiEye,
  FiEdit,
  FiX,
  FiMail,
  FiSearch,
  FiUpload,
  FiCheck,
} from "react-icons/fi";
import SharedLayout from "./SharedLayout";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "./services/apiClient";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import InfoModal from "./modals/InfoModals";
import ConfirmModal from "./modals/ConfirmModals";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import trLocale from "react-phone-input-2/lang/tr.json";
import { isValidE164, normalizePhone } from "./utils/normalizePhone";

/** Örnek Excel şablonu indir */
const handleDownloadTemplate = () => {
  const headers = ["Adı", "Soyadı", "E-posta", "Telefon Kodu", "Telefon"];
  const sample = [
    {
      Adı: "Ali",
      Soyadı: "Yılmaz",
      "E-posta": "ali@example.com",
      "Telefon Kodu": "+90",
      Telefon: "5000000000",
    },
  ];

  const ws = XLSX.utils.json_to_sheet(sample, { header: headers });
  ws["!cols"] = headers.map(() => ({ wch: 22 }));

  const notes = [
    ["Notlar"],
    ["- Bu şablondaki sütunları doldurup 'İçeri Aktar' ile yükleyin."],
    ["- Telefon Kodu ve Telefon birlikte kaydedilir (örn: +90 5000000000)."],
    ["- Departman seçenekleri: marketing, it, learning, other."],
  ];
  const wsNotes = XLSX.utils.aoa_to_sheet(notes);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sablon");
  XLSX.utils.book_append_sheet(wb, wsNotes, "Açıklama");

  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buf], { type: "application/octet-stream" });
  saveAs(blob, "ornek-katilimci-sablonu.xlsx");
};

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL; // Removed

/* ---------- Genel Modal ---------- */
const FormModal = ({ open, onClose, children, maxWidth = "max-w-lg" }) => {
  const onEsc = useCallback((e) => e.key === "Escape" && onClose(), [onClose]);
  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onEsc]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <div
        className={`relative w-[92%] ${maxWidth} bg-white rounded-3xl shadow-2xl p-6 sm:p-8`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
          aria-label="Kapat"
        >
          <FiX />
        </button>
        {children}
      </div>
    </div>
  );
};

const Learners = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  // Validation
  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());

  // State
  const [groups, setGroups] = useState([]);
  const [allLearners, setAllLearners] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [teamsLearners, setTeamsLearners] = useState([]);

  const [error, setError] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState("");

  // Info modal
  const [info, setInfo] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const showInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  // --- FETCH: all learners
  const fetchAllLearners = useCallback(async () => {
    if (!userId) return;
    try {
      // apiClient unwrap
      const data = await apiClient.get("/learners");

      // Backend returns array of learners directly or { learners: [] }?
      // learner.controller.ts getAllLearners returns `result`.
      // learner.service.ts getAllLearners returns `Learner[]`.
      // So data IS the array.
      // But Wait! The old code: res.data.learners
      // The NEW code: learner.service.ts: return repo.getAll(tenant.id); -> likely returns array.
      // I should check learner.service.ts to be sure.
      // Assuming it returns array directly (unwrapped by apiClient if wrapped in success:true,data:...).
      // If backend returns plain array, apiClient returns plain array.

      const list = Array.isArray(data) ? data : data.learners || [];

      const normalized = list.map((m) => ({
        id: m.id,
        name: m.name,
        surname: m.surname,
        email: m.email,
        number: m.number,
        active: !!m.active,
      }));
      setAllLearners(normalized);
    } catch (err) {
      console.error("Fetch learners error:", err);
      showInfo("error", "Hata", "Katılımcılar yüklenemedi.");
    }
  }, [userId]);

  // --- FETCH: groups
  const fetchGroups = useCallback(async () => {
    if (!userId) return;
    try {
      // apiClient unwrap
      const data = await apiClient.get("/groups");
      const groupsList = Array.isArray(data) ? data : data?.groups || [];

      const formatted = groupsList.map((group) => ({
        id: group.id,
        name: group.name,
        members: (group.members || []).map((m) => ({
          id: m.id,
          name: m.name,
          surname: m.surname,
          email: m.email,
          number: m.number,
          active: !!m.active,
        })),
      }));
      setGroups(formatted);
    } catch (err) {
      console.error("Error fetching groups:", err);
      setError("Gruplar yüklenirken bir hata oluştu.");
      showInfo("error", "Hata", "Gruplar yüklenemedi.");
    }
  }, [userId]);

  const fetchTeamsLearners = useCallback(async () => {
    if (!userId) return;

    try {
      const data = await apiClient.get("/teams/learners");
      setTeamsLearners(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching Teams learners:", err);
    }
  }, [userId]);

  // Teams durumunu learner ID'ye göre bul
  const getTeamsStatusForLearner = (learnerId) => {
    const teamsData = teamsLearners.find((t) => t.id === learnerId)?.teams;
    if (!teamsData) return "Teams eşleşmedi";
    if (teamsData.hasConversationReference) return "Hazır";
    if (teamsData.installStatus === "CONVERSATION_READY") return "Teams hazır";
    if (teamsData.installStatus === "NOT_LINKED") return "Teams eşleşmedi";
    return "Teams eşleşmedi";
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([fetchAllLearners(), fetchGroups(), fetchTeamsLearners()])
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [fetchAllLearners, fetchGroups, fetchTeamsLearners]);

  // Modals
  const [isLearnerOpen, setLearnerOpen] = useState(false);
  const [isGroupOpen, setGroupOpen] = useState(false);
  const [isImportOpen, setImportOpen] = useState(false);
  const [isAddToGroupOpen, setAddToGroupOpen] = useState(false);

  // Confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmKind, setConfirmKind] = useState(null);
  const [target, setTarget] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 8;

  const openConfirmLearner = (row) => {
    setConfirmKind("learner");
    setTarget(row);
    setConfirmOpen(true);
  };
  const openConfirmGroup = (group) => {
    setConfirmKind("group");
    setTarget(group);
    setConfirmOpen(true);
  };
  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmKind(null);
    setTarget(null);
  };

  // Inline edit
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    setEditingId(null);
    setDraft(null);
  }, [selectedGroupId]);

  const startEdit = (row) => {
    setEditingId(row.id);
    setDraft({ ...row });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
  };

  const saveEdit = async () => {
    if (!draft) return;
    if (!draft.name?.trim() || !draft.surname?.trim()) {
      showInfo("warning", "Eksik Bilgi", "Lütfen ad ve soyad girin.");
      return;
    }
    if (!draft.email?.trim() || !validateEmail(draft.email)) {
      showInfo("warning", "Geçersiz E-posta", "Geçerli e-posta girin.");
      return;
    }

    const normalized = draft.number ? normalizePhone(draft.number) : "";

    if (draft.number && !isValidE164(normalized)) {
      showInfo(
        "warning",
        "Geçersiz Telefon",
        "Lütfen geçerli bir telefon girin.",
      );
      return;
    }

    const updated = {
      ...draft,
      active: !!draft.active,
      name: draft.name.trim(),
      surname: draft.surname.trim(),
      email: draft.email.trim().toLowerCase(),
      number: normalized,
    };

    try {
      setIsLoading(true);
      // apiClient unwrap
      await apiClient.put(`/learners/${editingId}`, updated);

      showInfo("success", "Güncellendi", "Katılımcı güncellendi.");
      setEditingId(null);
      setDraft(null);

      await Promise.all([fetchAllLearners(), fetchGroups()]);
    } catch (error) {
      console.error("Update failed:", error);
      showInfo("error", "Hata", error.formatted?.message || "Güncellenemedi.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (row) => navigate(`/${userId}/learners/${row.id}/details`);

  const confirmDelete = async () => {
    if (!target) return;
    try {
      setIsLoading(true);

      if (confirmKind === "learner" && selectedGroupId) {
        // apiClient unwrap - DELETE with body needs `data` prop in config
        await apiClient.delete(`/groups/${selectedGroupId}/members`, {
          data: { learnerIds: [target.id] },
        });

        showInfo(
          "success",
          "Grup Üyeliği Kaldırıldı",
          `${target.name} ${target.surname} gruptan çıkarıldı.`,
        );
      } else if (confirmKind === "learner") {
        await apiClient.delete(`/learners/${target.id}`);

        showInfo(
          "success",
          "Katılımcı Silindi",
          `${target.name} ${target.surname} sistemden tamamen silindi.`,
        );
      } else if (confirmKind === "group") {
        await apiClient.delete(`/groups/${target.id}`);

        showInfo("success", "Grup Silindi", "Grup başarıyla silindi.");
      }

      await Promise.all([fetchAllLearners(), fetchGroups()]);
    } catch (error) {
      console.error("Delete error:", error);
      showInfo(
        "error",
        "Hata",
        error.formatted?.message || "Silme işlemi başarısız oldu.",
      );
    } finally {
      setIsLoading(false);
      closeConfirm();
    }
  };

  // Export: grup üyelerini indir
  const handleDownloadGroup = (group) => {
    if (!group?.members?.length) {
      showInfo("warning", "Boş Grup", "Bu grupta henüz katılımcı yok.");
      return;
    }
    const rows = group.members.map((m, i) => ({
      "#": i + 1,
      Adı: m.name,
      Soyadı: m.surname,
      Email: m.email,
      Telefon: m.number,
      Aktif: m.active ? "Evet" : "Hayır",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, group.name || "Grup");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buf], { type: "application/octet-stream" });
    saveAs(blob, `${group.name || "grup"}-katilimcilar.xlsx`);
  };

  // Yeni katılımcı
  const [newLearner, setNewLearner] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const submitNewLearner = async (e) => {
    e.preventDefault();
    if (!newLearner.firstName?.trim() || !newLearner.lastName?.trim()) {
      showInfo("warning", "Eksik Bilgi", "Ad & soyad zorunlu.");
      return;
    }
    if (!newLearner.email?.trim() || !validateEmail(newLearner.email)) {
      showInfo("warning", "Geçersiz E-posta", "Geçerli e-posta girin.");
      return;
    }

    const normalizedPhone = normalizePhone(newLearner.phone);

    if (newLearner.phone && !isValidE164(normalizedPhone)) {
      showInfo(
        "warning",
        "Geçersiz Telefon",
        "Lütfen geçerli bir numara girin.",
      );
      return;
    }

    if (newLearner.phone && !normalizedPhone) {
      showInfo(
        "warning",
        "Geçersiz Telefon",
        "Lütfen geçerli bir uluslararası numara girin (E.164).",
      );
      return;
    }

    try {
      setIsLoading(true);
      // apiClient unwrap
      await apiClient.post("/learners", {
        learners: [
          {
            name: newLearner.firstName.trim(),
            surname: newLearner.lastName.trim(),
            email: newLearner.email.trim().toLowerCase(),
            number: normalizedPhone,
            adminId: Number(userId),
          },
        ],
        adminId: Number(userId),
      });

      showInfo("success", "Başarılı", "Katılımcı eklendi.");
      setNewLearner({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        phoneCode: "90",
      });
      setLearnerOpen(false);

      await fetchAllLearners();
    } catch (error) {
      const backendMessage =
        error.formatted?.message ||
        error.formatted?.error ||
        "Katılımcı eklenemedi.";

      showInfo("error", "Hata", backendMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Import
  const [importFile, setImportFile] = useState(null);
  const [importDrag, setImportDrag] = useState(false);
  const submitImport = async (e) => {
    e.preventDefault();
    if (!importFile) {
      showInfo("warning", "Dosya Gerekli", "Lütfen dosya seçin.");
      return;
    }

    try {
      setIsLoading(true);
      const data = await importFile.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);

      const learners = rows
        .map((r) => {
          const raw = `${(r["Telefon Kodu"] || "").toString().trim()}${(
            r["Telefon"] || ""
          )
            .toString()
            .trim()}`;
          const normalized = normalizePhone(raw);

          return {
            name: (r["Adı"] || "").toString().trim(),
            surname: (r["Soyadı"] || "").toString().trim(),
            email: (r["E-posta"] || "").toString().trim().toLowerCase() || null,
            number: normalized || "", // invalid ise boş kaydediyoruz
            adminId: Number(userId),
          };
        })
        .filter((x) => x.name && x.surname && x.number);

      if (learners.length === 0) {
        showInfo("warning", "Boş Dosya", "Geçerli kayıt bulunamadı.");
        return;
      }

      await apiClient.post("/learners", {
        learners,
        adminId: Number(userId),
      });

      showInfo("success", "Başarılı", `${learners.length} katılımcı eklendi.`);
      setImportFile(null);
      setImportOpen(false);

      await fetchAllLearners();
    } catch (err) {
      console.error("Import error:", err);
      const msg =
        err.formatted?.message ||
        err.message ||
        "Excel yüklenemedi. Dosya formatını kontrol edin.";
      showInfo("error", "Hata", msg);
    } finally {
      setIsLoading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setImportDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setImportFile(f);
  };

  // Arama ve tablo verisi
  const [query, setQuery] = useState("");
  const norm = (s) => (s || "").toString().toLowerCase();

  const selectedGroup = groups.find(
    (g) => String(g.id) === String(selectedGroupId),
  );

  const baseRows = selectedGroupId ? selectedGroup?.members || [] : allLearners;

  const tableRows = baseRows.filter((l) => {
    const q = norm(query);
    if (!q) return true;
    return (
      norm(l.name).includes(q) ||
      norm(l.surname).includes(q) ||
      norm(l.email).includes(q) ||
      norm(l.number).includes(q)
    );
  });

  // Sayfalama için slice
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentRows = tableRows.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(tableRows.length / rowsPerPage);

  const getPaginationItems = (page, total) => {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const items = [1];
    if (page <= 4) {
      items.push(2, 3, 4);
      items.push("dots");
    } else if (page >= total - 3) {
      items.push("dots", total - 3, total - 2, total - 1);
    } else {
      items.push("dots", page - 1, page, page + 1, "dots");
    }
    items.push(total);
    return items.filter((value, index, self) => self.indexOf(value) === index);
  };

  const paginationItems = getPaginationItems(currentPage, totalPages);

  // Gruba katılımcı ekleme modalı
  const [candidateQuery, setCandidateQuery] = useState("");
  const groupCandidates = selectedGroupId
    ? allLearners.filter(
        (m) => !(selectedGroup?.members || []).some((x) => x.id === m.id),
      )
    : [];

  return (
    <SharedLayout pageTitle="KATILIMCILAR">
      <div className="bg-gray-50 p-8 rounded-2xl shadow-sm">
        {/* Üst Butonlar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <button
            className="flex items-center justify-center p-4 bg-white rounded-full shadow-sm hover:shadow-md transition-all"
            onClick={() => setGroupOpen(true)}
          >
            <FiUsers className="mr-2 text-blue-500" size={18} />
            <span className="font-medium">YENİ GRUP</span>
          </button>
          <button
            className="flex items-center justify-center p-4 bg-white rounded-full shadow-sm hover:shadow-md transition-all"
            onClick={() => setLearnerOpen(true)}
          >
            <FiPlus className="mr-2 text-green-500" size={18} />
            <span className="font-medium">YENİ KATILIMCI</span>
          </button>
          <button
            className="flex items-center justify-center p-4 bg-white rounded-full shadow-sm hover:shadow-md transition-all"
            onClick={() => setImportOpen(true)}
          >
            <FiDownload className="mr-2 text-purple-500" size={18} />
            <span className="font-medium">İÇERİ AKTAR</span>
          </button>
        </div>

        {/* Grup kartları */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            SINIF LİSTELERİNİZ
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {groups.map((group) => {
              const active = String(group.id) === String(selectedGroupId);
              return (
                <div
                  key={group.id}
                  className={`p-6 rounded-xl shadow-sm cursor-pointer transition-colors ${
                    active ? "bg-green-200" : "bg-white hover:bg-green-50"
                  }`}
                  onClick={() =>
                    setSelectedGroupId(active ? "" : String(group.id))
                  }
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-bold text-green-500">{group.name}</h3>
                    <div className="flex space-x-2">
                      <FiDownload
                        className="text-gray-400 hover:text-blue-500 cursor-pointer"
                        title="Excel indir"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadGroup(group);
                        }}
                      />
                      <FiTrash2
                        className="text-gray-400 hover:text-red-500 cursor-pointer"
                        title="Grubu sil"
                        onClick={(e) => {
                          e.stopPropagation();
                          openConfirmGroup(group);
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex items-end">
                    <span className="text-lg font-medium">
                      KATILIMCI SAYISI:
                    </span>
                    <span className="text-3xl font-bold ml-2">
                      {group.members.length}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Gruba Katılımcı Ekle */}
        {selectedGroupId && (
          <div className="mb-4">
            <button
              onClick={() => setAddToGroupOpen(true)}
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg shadow hover:bg-emerald-600 disabled:opacity-50"
              disabled={groupCandidates.length === 0}
            >
              Gruba Katılımcı Ekle
            </button>
            {groupCandidates.length === 0 && (
              <span className="ml-3 text-sm text-gray-500">
                Eklenecek uygun katılımcı bulunamadı.
              </span>
            )}
          </div>
        )}

        {/* Arama + Tablo */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="p-4 border-b border-gray-200">
            {/* 🔍 Arama */}
            <div className="relative max-w-sm">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Katılımcı ara…"
                className="w-full pl-10 pr-3 py-2 rounded-full border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Ad</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Soyad</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Telefon</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Aktif</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Teams Durumu</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">İşlemler</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center">
                      Katılımcı bulunamadı.
                    </td>
                  </tr>
                ) : (
                  currentRows.map((row) => {
                    const isEditing = editingId === row.id;
                    const data = isEditing ? draft : row;
                    return (
                      <tr key={row.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {isEditing ? (
                            <input
                              value={data.name}
                              onChange={(e) =>
                                setDraft((d) => ({
                                  ...d,
                                  name: e.target.value,
                                }))
                              }
                              className="w-full border border-gray-300 rounded-md px-2 py-1"
                            />
                          ) : (
                            <span className="font-medium">{data.name}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {isEditing ? (
                            <input
                              value={data.surname}
                              onChange={(e) =>
                                setDraft((d) => ({
                                  ...d,
                                  surname: e.target.value,
                                }))
                              }
                              className="w-full border border-gray-300 rounded-md px-2 py-1"
                            />
                          ) : (
                            data.surname
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {isEditing ? (
                            <input
                              value={data.number}
                              onChange={(e) =>
                                setDraft((d) => ({
                                  ...d,
                                  number: e.target.value,
                                }))
                              }
                              className="w-full border border-gray-300 rounded-md px-2 py-1"
                            />
                          ) : (
                            data.number
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {isEditing ? (
                            <input
                              type="email"
                              value={data.email}
                              onChange={(e) =>
                                setDraft((d) => ({
                                  ...d,
                                  email: e.target.value,
                                }))
                              }
                              className="w-full border border-gray-300 rounded-md px-2 py-1"
                            />
                          ) : (
                            data.email
                          )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <label
                            className={`inline-flex items-center ${
                              isEditing
                                ? "cursor-pointer"
                                : "cursor-not-allowed opacity-60"
                            }`}
                          >
                            <input
                              type="checkbox"
                              disabled={!isEditing}
                              checked={!!data.active}
                              onChange={(e) =>
                                setDraft((d) => ({
                                  ...d,
                                  active: e.target.checked,
                                }))
                              }
                              className="peer sr-only"
                              aria-label="Active"
                            />
                            <span
                              className={`h-5 w-5 rounded border grid place-items-center transition-colors ${
                                isEditing
                                  ? "border-gray-300 peer-checked:bg-orange-500 peer-checked:border-orange-500"
                                  : "border-gray-300 pointer-events-none"
                              }`}
                              aria-hidden="true"
                            >
                              <svg
                                className="w-4 h-4 text-white opacity-0 transition-opacity peer-checked:opacity-100"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-7.364 7.364a1 1 0 01-1.414 0L3.293 9.829a1 1 0 111.414-1.414l3.222 3.222 6.657-6.657a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </span>
                          </label>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              getTeamsStatusForLearner(row.id) === "Hazır"
                                ? "bg-green-100 text-green-700"
                                : getTeamsStatusForLearner(row.id) === "Teams hazır"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {getTeamsStatusForLearner(row.id)}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-md text-gray-500">
                          <div className="flex items-center space-x-3">
                            {isEditing ? (
                              <>
                                <FiCheck
                                  className="text-emerald-600 hover:text-emerald-700 cursor-pointer"
                                  title="Kaydet"
                                  onClick={saveEdit}
                                />
                                <FiX
                                  className="text-gray-500 hover:text-gray-700 cursor-pointer"
                                  title="İptal"
                                  onClick={cancelEdit}
                                />
                              </>
                            ) : (
                              <>
                                <FiEye
                                  className="text-orange-500 hover:text-orange-700 cursor-pointer"
                                  onClick={() => handleView(row)}
                                  title="Detay gör"
                                />
                                <FiEdit
                                  className="text-orange-500 hover:text-orange-700 cursor-pointer"
                                  onClick={() => startEdit(row)}
                                  title="Düzenle"
                                />
                                <FiTrash2
                                  className="text-red-500 hover:text-red-600 cursor-pointer"
                                  onClick={() => openConfirmLearner(row)}
                                  title="Sil"
                                />
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ✅ Pagination kontrolü */}
          {totalPages > 1 && (
            <div className="border-t bg-gray-50">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-6 py-3">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                >
                  Önceki
                </button>

                <div className="hidden sm:flex items-center gap-2">
                  {paginationItems.map((item, idx) =>
                    item === "dots" ? (
                      <span
                        key={`dots-${idx}`}
                        className="px-3 py-1 rounded bg-transparent text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setCurrentPage(item)}
                        className={`px-3 py-1 rounded ${
                          currentPage === item
                            ? "bg-emerald-500 text-white"
                            : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                        }`}
                      >
                        {item}
                      </button>
                    ),
                  )}
                </div>

                <span className="text-sm text-gray-600 sm:hidden">
                  Sayfa {currentPage} / {totalPages}
                </span>

                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                >
                  Sonraki
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Yeni Katılımcı Modal*/}
      <FormModal
        open={isLearnerOpen}
        onClose={() => setLearnerOpen(false)}
        maxWidth="max-w-md"
      >
        <h3 className="text-center text-2xl font-semibold mb-6">
          Yeni Katılımcı
        </h3>
        <form onSubmit={submitNewLearner} className="space-y-4">
          <input
            type="text"
            placeholder="Adı"
            required
            value={newLearner.firstName}
            onChange={(e) =>
              setNewLearner({ ...newLearner, firstName: e.target.value })
            }
            className="w-full rounded-xl border border-gray-400 px-4 py-3"
          />
          <input
            type="text"
            placeholder="Soyadı"
            required
            value={newLearner.lastName}
            onChange={(e) =>
              setNewLearner({ ...newLearner, lastName: e.target.value })
            }
            className="w-full rounded-xl border border-gray-400 px-4 py-3"
          />
          <div className="relative">
            <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              placeholder="asd@mail.com"
              required
              value={newLearner.email}
              onChange={(e) =>
                setNewLearner({ ...newLearner, email: e.target.value })
              }
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <PhoneInput
            country={"tr"}
            localization={trLocale}
            countryCodeEditable={false}
            value={newLearner.phone}
            onChange={(value) => setNewLearner({ ...newLearner, phone: value })}
            inputStyle={{ width: "100%", height: "48px" }}
            buttonStyle={{ borderRadius: "12px 0 0 12px" }}
            dropdownStyle={{ maxHeight: "200px" }}
            placeholder="Telefon numarası"
          />

          <div className="pt-2 flex justify-center">
            <button
              type="submit"
              className="px-10 h-12 rounded-xl bg-emerald-500 text-white font-medium hover:bg-emerald-600"
              disabled={isLoading}
            >
              {isLoading ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </FormModal>

      {/* Grup Oluştur Modal */}
      <FormModal
        open={isGroupOpen}
        onClose={() => setGroupOpen(false)}
        maxWidth="max-w-lg"
      >
        <h3 className="text-center text-2xl font-semibold mb-6">
          Grup Oluştur
        </h3>
        <GroupCreateForm
          userId={userId}
          onSuccess={async () => {
            setGroupOpen(false);
            await fetchGroups();
          }}
          showInfo={showInfo}
        />
      </FormModal>

      {/* İçeri Aktar Modal*/}
      <FormModal
        open={isImportOpen}
        onClose={() => setImportOpen(false)}
        maxWidth="max-w-xl"
      >
        <div className="flex items-center justify-between mb-6 mr-3">
          <h3 className="text-2xl font-semibold">Yeni Katılımcılar Ekle!</h3>
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="text-emerald-600 hover:text-emerald-700 underline text-sm"
          >
            Örnek Excel Şablonu indir
          </button>
        </div>

        <form onSubmit={submitImport} className="space-y-6">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setImportDrag(true);
            }}
            onDragLeave={() => setImportDrag(false)}
            onDrop={onDrop}
            className={`rounded-2xl border-2 border-dashed p-8 text-center transition ${
              importDrag
                ? "border-emerald-400 bg-emerald-50/30"
                : "border-gray-300 bg-gray-50/40"
            }`}
          >
            <div className="flex flex-col items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-white border border-gray-300 grid place-items-center text-gray-500">
                <FiUpload />
              </div>
              <p className="text-sm sm:text-base">
                Sürükle &amp; Bırak veya{" "}
                <label className="text-emerald-600 font-medium underline cursor-pointer">
                  Dosya Seç
                  <input
                    type="file"
                    accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="hidden"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  />
                </label>{" "}
                yüklemek için
              </p>
              <p className="text-xs text-gray-500">xls, xlsx</p>
              {importFile && (
                <p className="mt-2 text-xs sm:text-sm text-gray-700">
                  Seçilen:{" "}
                  <span className="font-medium">{importFile.name}</span>
                </p>
              )}
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <button
              type="submit"
              className="px-8 h-11 rounded-xl bg-emerald-500 text-white font-medium hover:bg-emerald-600 disabled:opacity-60"
              disabled={!importFile || isLoading}
            >
              {isLoading ? "Yükleniyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </FormModal>

      {/* Gruba Katılımcı Ekle Modal */}
      <FormModal
        open={isAddToGroupOpen}
        onClose={() => setAddToGroupOpen(false)}
        maxWidth="max-w-xl"
      >
        <h3 className="text-xl font-semibold mb-4">Gruba Katılımcı Ekle</h3>
        {groupCandidates.length === 0 ? (
          <p className="text-center text-gray-500">
            Eklenecek uygun katılımcı yok.
          </p>
        ) : (
          <>
            {/* 🔍 Arama alanı */}
            <div className="relative mb-4">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Katılımcı ara..."
                value={candidateQuery}
                onChange={(e) => setCandidateQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <ul className="max-h-80 overflow-y-auto divide-y">
              {groupCandidates
                .filter((m) => {
                  const q = candidateQuery.toLowerCase();
                  return (
                    m.name?.toLowerCase().includes(q) ||
                    m.surname?.toLowerCase().includes(q) ||
                    m.email?.toLowerCase().includes(q)
                  );
                })
                .map((m) => (
                  <li
                    key={m.id}
                    className="flex justify-between items-center p-2"
                  >
                    <span>
                      {m.name} {m.surname} —{" "}
                      <span className="text-gray-500 text-sm">{m.email}</span>
                    </span>
                    <button
                      onClick={async () => {
                        try {
                          // apiClient unwrap
                          await apiClient.post(
                            `/groups/${selectedGroupId}/members`,
                            { learnerIds: [m.id] },
                          );
                          showInfo(
                            "success",
                            "Eklendi",
                            "Katılımcı gruba eklendi.",
                          );
                          setAddToGroupOpen(false);
                          await fetchGroups();
                        } catch (err) {
                          console.error("Add member error:", err);
                          showInfo("error", "Hata", "Katılımcı eklenemedi.");
                        }
                      }}
                      className="px-3 py-1 text-sm bg-emerald-500 text-white rounded hover:bg-emerald-600"
                    >
                      Ekle
                    </button>
                  </li>
                ))}
            </ul>
          </>
        )}
      </FormModal>

      {/* Confirm */}
      <ConfirmModal
        open={confirmOpen}
        title="Onay"
        description={
          target
            ? selectedGroupId
              ? `${target.name} ${target.surname} isimli kullanıcıyı gruptan çıkarmak istediğinize emin misiniz?`
              : `${target.name} ${target.surname} isimli kullanıcıyı silmek istediğinize emin misiniz?`
            : "Bu işlemi gerçekleştirmek istediğinize emin misiniz?"
        }
        confirmText={selectedGroupId ? "Gruptan Çıkar" : "Sil"}
        confirmBtnClass={
          selectedGroupId
            ? "bg-orange-500 hover:bg-orange-600 text-white"
            : "bg-red-600 hover:bg-red-700 text-white"
        }
        cancelBtnClass="border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
        onCancel={closeConfirm}
        onConfirm={confirmDelete}
      />

      {/* Loading overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/20 z-40 flex items-center justify-center">
          <div className="bg-white rounded-lg p-4 shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Yükleniyor...</p>
          </div>
        </div>
      )}

      {/* Error toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center">
            <span className="text-sm">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-2 text-red-500 hover:text-red-700"
            >
              <FiX />
            </button>
          </div>
        </div>
      )}

      {/* Info modal */}
      <InfoModal
        open={info.open}
        type={info.type}
        title={info.title}
        message={info.message}
        onClose={closeInfo}
      />
    </SharedLayout>
  );
};

/* Grup oluşturma formu */
const GroupCreateForm = ({ userId, onSuccess, showInfo }) => {
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const adminId = Number(userId);
    if (!name.trim()) {
      showInfo("warning", "Eksik Bilgi", "Lütfen bir grup adı girin.");
      return;
    }
    if (!Number.isInteger(adminId)) {
      showInfo("error", "Hata", "Geçersiz yönetici bilgisi.");
      return;
    }
    try {
      setIsLoading(true);
      // apiClient unwrap
      await apiClient.post("/groups", {
        name: name.trim(),
        adminId,
      });
      // success if no error thrown
      showInfo("success", "Başarılı", "Grup oluşturuldu.");
      setName("");
      onSuccess?.();
    } catch (err) {
      showInfo(
        "error",
        "Hata",
        err.formatted?.message || "Grup oluşturulamadı.",
      );
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-6">
      <input
        type="text"
        placeholder="Grup Adı"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full rounded-xl border border-gray-400 px-4 py-3"
        required
      />
      <div className="flex justify-center">
        <button
          type="submit"
          disabled={isLoading}
          className="px-10 h-12 rounded-xl bg-emerald-500 text-white font-medium hover:bg-emerald-600"
        >
          {isLoading ? "Oluşturuluyor..." : "Kaydet"}
        </button>
      </div>
    </form>
  );
};

export default Learners;
